import { formatValidUntil, pluralCredits } from '../schemas/credits.js';
import type { PayCurrency, PayMethod } from '../schemas/payments.js';
import type { RecordingProblem } from '../schemas/voice.js';
import { formatAmount } from '../payments.js';

const n = (credits: number) => `${credits} ${pluralCredits(credits)}`;

/** 1 идея, 3 идеи, 5 идей. */
const ideasWord = (count: number) => {
  const m10 = count % 10;
  const m100 = count % 100;
  if (m10 === 1 && m100 !== 11) return 'идея';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'идеи';
  return 'идей';
};

const METHOD_LABELS: Record<PayMethod, string> = {
  kaspi: 'Kaspi',
  lava_card: 'Карта РФ',
  lava_sbp: 'СБП',
  lava_usd: 'Карта другой страны',
  stars: 'Telegram Stars',
};

/** createInvoiceLink takes a title of 1–32 characters. */
const STARS_TITLE_MAX = 32;

/** Texts the API and the worker send to people through the bot. */
export const ru = {
  creditsExpiring: (credits: number, expiresAt: Date | string): string =>
    `⏳ У тебя ${n(credits)}, они сгорят ${formatValidUntil(expiresAt)} в 23:59. Успей потратить их на рилсы и карусели.`,

  monthlyGrant: (credits: number, expiresAt: Date | string): string =>
    `🎁 Тебе начислено ${n(credits)} на этот месяц. Они действуют до ${formatValidUntil(expiresAt)} включительно.`,

  /** The owner added credits by hand in «Участники». */
  creditsAdded: (credits: number, expiresAt: Date | string): string =>
    `🎁 Тебе добавлено ${n(credits)}. Они действуют до ${formatValidUntil(expiresAt)} включительно.`,

  accessOpened: (credits: number): string =>
    credits > 0
      ? `Доступ открыт 🎉\n\nНа балансе ${n(credits)}. Открой приложение и наговори первую мысль.`
      : 'Доступ открыт 🎉\n\nОткрой приложение и наговори первую мысль.',

  ownerNewUser: (label: string): string => `👤 Новый пользователь: ${label}`,

  /** The waitlist pass let the person in (spec 9.2); sent in the daytime. */
  letIn: (credits: number): string =>
    credits > 0
      ? `Привет! Место освободилось — ты внутри 🎉 На балансе ${n(credits)}.`
      : 'Привет! Место освободилось — ты внутри 🎉',
  letInOpenButton: 'Открыть',
  ownerWaitlistPass: (admitted: number, waiting: number): string =>
    `🚪 Впустил ${admitted} из листа, в листе осталось ${waiting}`,

  ownerWaitlist: (label: string, note: string | null): string =>
    note ? `📝 Заявка в лист ожидания: ${label}\n«${note}»` : `📝 Заявка в лист ожидания: ${label}`,

  /** A failure of the brain; `suppressed` — how many of the same kind were not sent in the last 5 minutes. */
  ownerFailure: (where: string, detail: string, suppressed: number): string =>
    `⚠️ Сбой: ${where}\n${detail}` +
    (suppressed > 0 ? `\n\nЕщё ${suppressed} таких же сбоев за 5 минут — без отдельных сообщений.` : ''),

  ownerNoBalance: '💸 На счёте OpenRouter закончились деньги: мысли и тексты не разбираются, пока баланс не пополнен.',

  ownerNoKey: '🔑 Не задан ключ OpenRouter (OPENROUTER_API_KEY): мысли и тексты не разбираются.',

  // ---- thoughts from the chat ----

  ideasReady: (count: number): string => `Готово: ${count} ${ideasWord(count)} 💡 Открой и выбери, с какой работать.`,

  openIdeasButton: 'Открыть идеи',

  thoughtEmpty: 'Не расслышал мысль: запись пустая или слишком тихая. Запиши ещё раз — кредиты не списаны.',

  thoughtBadAudio: 'Не смог прочитать запись. Пришли её ещё раз голосовым — кредиты не списаны.',

  thoughtFailed: 'Не получилось разобрать мысль. Попробуй ещё раз через пару минут — кредиты не списаны.',

  // ---- the spoken introduction ----

  introSaved: (about: string): string =>
    `Запомнил 🙌\n\n«${about}»\n\nПоправить можно в приложении, в настройках.`,

  introEmpty: 'Не расслышал рассказ о тебе. Запиши ещё раз, 20–30 секунд.',

  introFailed: 'Не получилось сохранить рассказ о тебе. Попробуй ещё раз позже.',

  // --- Voice (stage 4) ---

  /** Read aloud for the voice sample: about 22–25 seconds at a calm pace. */
  voiceSampleText:
    'Каждое утро я начинаю с короткой прогулки. Двадцать минут без телефона помогают собраться с мыслями и понять, что сегодня действительно важно. Потом я сажусь за работу и делаю самое трудное, пока голова свежая. Вечером подвожу итог: что получилось, что нет и почему. Такой простой порядок экономит мне несколько часов в неделю.',

  /** Google's consent statement for ru-RU, word for word (VOICE_CONSENT_VERSION). */
  voiceConsentPhrase:
    'Я являюсь владельцем этого голоса и даю согласие Google на использование этого голоса для создания модели синтетического голоса.',

  /** Spoken by a fresh clone for the "sounds like me?" step. */
  voicePreviewText:
    'Привет! Так будет звучать мой голос в роликах. Три коротких мысли, тридцать секунд — и готово.',

  /**
   * The sample of a manner (spec 5.2, 5.3): every stock voice and every clone reads it in each
   * manner once. Numbers are in words: the samples skip the text preparation.
   */
  voiceMannerSampleText:
    'Знаешь, почему одни ролики досматривают до конца, а другие пролистывают? Всё решают первые три секунды. Сейчас покажу, как их не потерять.',

  ownerVoiceConsentMismatch: (userId: number): string =>
    `🎙 Google не принял согласие: голос в согласии не совпал с образцом (пользователь #${userId}). Человеку показана просьба перезаписать.`,

  ownerVoiceUnavailable: (userId: number, detail: string): string =>
    `🎙 Голос недоступен (пользователь #${userId}): ${detail}`,

  ownerVoiceRefused: (userId: number, detail: string): string =>
    `🚨 Google отказал в голосе (пользователь #${userId}): ${detail}`,

  ownerVoiceGross: (userId: number, voiceoverId: number): string =>
    `🎙 Озвучка #${voiceoverId} дважды вышла с пропущенным куском текста (пользователь #${userId}). Отдана человеку с предупреждением: послушать и решить.`,

  ownerVoiceTakeFailed: (userId: number, voiceoverId: number, detail: string): string =>
    `🚨 Озвучка #${voiceoverId} упала (пользователь #${userId}): ${detail}`,

  // --- Voice sample through the chat (spec 6.3) ---

  voiceChatAskSample: (sampleText: string): string =>
    `🎙 Запишем твой голос здесь, в чате.\n\n1. Пришли голосовое: прочитай вслух текст ниже — так, как хочешь звучать в роликах. Около 25 секунд, в тихом месте.\n\n${sampleText}`,

  voiceChatAskConsent: (phrase: string): string =>
    `Отлично! 2. Сразу, там же, пришли второе голосовое — прочитай слово в слово:\n\n«${phrase}»`,

  voiceChatDone: 'Голос создан 🎉 Открой настройки: послушай и подтверди, что он похож.',

  voiceChatOpenSettings: 'Открыть настройки',

  voiceChatRetry: (problem: string): string => `${problem} Пришли это голосовое ещё раз.`,

  voiceChatProblems: {
    unreadable: 'Не получилось прочитать запись.',
    too_short: 'Слишком коротко — нужен весь текст целиком.',
    too_long: 'Слишком длинно — прочитай только текст из сообщения.',
    silent: 'Запись пустая — похоже, микрофон не сработал.',
    too_quiet: 'Слишком тихо — держи телефон ближе.',
    too_loud: 'Звук перегружен — держи телефон чуть дальше.',
    noisy: 'Слишком шумно — найди место потише.',
    wrong_text: 'Похоже, прочитан другой текст.',
  } satisfies Record<RecordingProblem, string>,

  voiceChatRestart: 'Начнём сначала: пришли образец ещё раз, а сразу за ним — согласие.',

  voiceChatMismatch:
    'Google не принял запись согласия: фразу нужно прочитать слово в слово, тем же голосом, что и образец. Начнём сначала: пришли образец ещё раз, а сразу за ним — согласие.',

  voiceChatLimit: 'На сегодня попытки создать голос закончились. Попробуй завтра.',

  voiceChatUnavailable:
    'Сервис озвучки сейчас недоступен. Через несколько минут пришли согласие ещё раз.',

  voiceChatExpired:
    'Время записи голоса в чате вышло. Открой приложение → Настройки → «Образец голоса» и начни заново.',

  // --- Build (stage 3, spec 6.1, 12) ---

  /** A line added to every failure that gave credits back. */
  renderRefunded: (credits: number): string => (credits > 0 ? `\n\nКредиты вернулись: ${n(credits)}.` : ''),

  /** The engine's own words, then what to do (spec 5.2: «Экран «Текст» на указанном блоке»). */
  renderFailedText: (engineMessage: string): string => `Не получилось собрать: ${engineMessage}\n\nПоправь текст и собери ещё раз.`,

  renderFailedVoice:
    'Озвучка не прошла проверку при сборке. Нажми «Собрать» ещё раз — новая озвучка сделается сама.',

  renderFailedOwnVoice: (engineMessage: string): string => `Не получилось собрать: ${engineMessage}`,

  /** «Собрать» waited for its take (0710), and the take failed: nothing was built. */
  renderVoiceTakeFailed: 'Не получилось озвучить текст, поэтому ролик не собран. Нажми «Собрать» ещё раз.',

  /** «Собрать» waited for its take, and the synthesizer lost a piece of the text twice (2026-10-05). */
  renderVoiceTakeGross:
    'Похоже, в озвучке пропал кусок текста, поэтому ролик не собран. Послушай её: всё в порядке — нажми «Собрать», нет — «Переозвучить».',

  renderFailedLater: 'Сервис сборки сейчас недоступен. Попробуй собрать через несколько минут.',

  renderFailedSorry: 'Не получилось собрать — мы уже разбираемся.',

  /** When the bot could not send the files: the result is in the app anyway. */
  renderReadyFallback: 'Готово ✅ Результат ждёт в приложении.',

  renderOpenText: 'Открыть текст',
  renderOpenStyle: 'Собрать ещё раз',
  renderOpenOwnTake: 'Записать заново',
  renderOpenResult: 'Открыть в приложении',

  /** The pill on a free build (spec 10.3): drawn by the worker on every video and picture. */
  watermarkText: (botUsername: string): string => `Сделано в @${botUsername}`,

  /** `what` names a build of a type other than «Сцены»: «Схема · Техно», «Подборка · Стекло». */
  ownerRenderFailed: (jobId: number, userId: number, error: string, detail: string, what?: string): string =>
    `⚠️ Сборка #${jobId}${what ? ` «${what}»` : ''} (пользователь ${userId}) не удалась: ${error}\n${detail}`.slice(0, 3500),

  /** The reel types other than «Сцены», as the owner's messages name them. */
  typeNames: { diagram: 'Схема', listicle: 'Подборка' },

  /** Criterion 4 of spec 3 is the owner's: every dislike and every comment at once. */
  ownerRenderDisliked: (jobId: number, userId: number, what?: string): string =>
    `👎 сборка #${jobId}${what ? ` «${what}»` : ''}, пользователь ${userId}`,

  ownerRenderComment: (jobId: number, comment: string, what?: string): string =>
    `💬 к сборке #${jobId}${what ? ` «${what}»` : ''}: ${comment}`.slice(0, 3500),

  ownerPassportFailed: (detail: string): string =>
    `⚠️ Воркер не прочитал паспорт движка: ${detail}`.slice(0, 3500),

  ownerAlignUnavailable: (detail: string): string =>
    `⚠️ Сверка озвучки недоступна (воркер или движок): ${detail}`.slice(0, 3500),

  // ---- payments (spec 2026-10-12 «Оплата») ----

  /** «7 490 ₸», «1 490 ₽», «$17», «1 300 ⭐». */
  amountLabel: (amount: number, currency: PayCurrency): string => formatAmount(amount, currency),

  methodLabel: (method: PayMethod): string => METHOD_LABELS[method],

  /** The bot to the person once the credits are on the balance. */
  paymentDone: (credits: number, balance: number, code: string): string =>
    `Оплата прошла ✅ +${n(credits)}. На балансе ${balance}. Заказ №${code}.`,

  /** The button under «Оплата прошла»: opens the Mini App on «Кредиты». */
  paymentOpenButton: 'Открыть',

  /** What the person sees in Kaspi. Never «Заказ»: the sales bot finds its own invoices by that word. */
  kaspiComment: (brand: string, packTitle: string, code: string): string => `${brand} · ${packTitle} · №${code}`,

  /** Telegram allows 32 characters: the pack title is cut, the brand stays. */
  starsInvoiceTitle: (brand: string, packTitle: string): string => {
    const head = `${brand} · `;
    if (head.length >= STARS_TITLE_MAX) return brand.slice(0, STARS_TITLE_MAX);
    return head + packTitle.slice(0, STARS_TITLE_MAX - head.length).trimEnd();
  },

  starsInvoiceDescription: (credits: number): string =>
    `${n(credits)} на ролики и карусели из голоса. Кредиты придут на баланс сразу после оплаты.`.slice(0, 255),

  /** The answer to Telegram's pre-checkout when the order is gone, paid, someone else's or another amount. */
  starsOrderStale: 'Счёт устарел, открой покупку заново.',

  ownerPayment: (code: string, method: string, amount: string, packTitle: string, who: string): string =>
    `💰 №${code} · ${method} · ${amount} · «${packTitle}» · ${who}`,

  /** A payment that is surely ours but cannot be tied to an order (spec 5.6). */
  ownerPaymentNoOrder: (provider: string, detail: string): string =>
    `💰 Оплата без заказа: ${provider}\n${detail}\n\nКредиты сами не пришли — выдай вручную в «Участниках».`.slice(0, 3500),

  /** The facts of a payment or refund that has no order: provider ids, amount, e-mail, our code. */
  noOrderDetail: (p: { operation?: string; contract?: string; amount?: string; email?: string; code?: string }): string =>
    [
      p.code && `заказ №${p.code} не найден`,
      p.operation && `операция ${p.operation}`,
      p.contract && `контракт ${p.contract}`,
      p.amount && `сумма ${p.amount}`,
      p.email && `почта ${p.email}`,
    ]
      .filter(Boolean)
      .join(', '),

  /** A lava.top refund of our product with no paid order of that e-mail. */
  ownerRefundNoOrder: (detail: string): string =>
    `↩️ Возврат lava.top без заказа: ${detail}. Оплаченный заказ с этой почтой не найден — проверь вручную.`.slice(0, 3500),

  ownerRefund: (code: string, taken: number, spent: number, credits: number): string =>
    `↩️ Возврат по №${code}: снято ${n(taken)}, потрачено ${spent} из ${credits}`,

  ownerChargeback: (code: string, taken: number, spent: number, credits: number): string =>
    `⚠️ Чарджбэк по №${code}: покупатель оспорил платёж в банке; снято ${n(taken)}, потрачено ${spent} из ${credits}`,

  ownerPartialRefund: (code: string, amount: string): string =>
    `Частичный возврат ${amount} по №${code}. Кредиты не тронуты`,

  /** A lava.top refund carries only the e-mail: several paid orders fit it. */
  ownerRefundAmbiguous: (email: string, codes: string[]): string =>
    `↩️ Возврат lava.top для ${email}: подходят заказы ${codes.map((c) => `№${c}`).join(', ')}. ` +
    'Кредиты не тронуты — проверь в кабинете lava.top, какой заказ вернули, и нажми у него «Снять кредиты» в «Оплатах».',

  ownerKaspiDown:
    '🔌 Касса Kaspi отключилась: счета Kaspi не выставляются. Войди заново по SMS в кабинете Aitole ' +
    'и не входи в Kaspi Pay под номером кассира.',

  ownerKaspiBack: '🔌 Касса Kaspi снова работает.',

  /** One Stars invoice paid twice (two devices): the second payment got no credits. */
  ownerStarsPaidTwice: (code: string, chargeId: string, stars: string): string =>
    `⚠️ Заказ №${code} оплачен звёздами второй раз (${stars}, платёж ${chargeId}): кредиты за второй раз не начислены. ` +
    'Верни звёзды через Telegram или выдай кредиты вручную в «Участниках».',

  /** We learned of a Kaspi payment only after it was refunded in the Aitole cabinet. */
  ownerPaidButRefunded: (code: string): string =>
    `↩️ Оплата Kaspi по №${code} уже возвращена в кабинете Aitole — кредиты за неё не начислены.`,

  /** Aitole did not answer twice: the invoice may still have reached the phone (one message per order). */
  ownerKaspiUnsure: (code: string, amount: string, mask: string): string =>
    `⚠️ Счёт Kaspi №${code} на ${amount} (${mask}): Aitole не ответил, счёт мог дойти до человека. ` +
    'Если в кабинете Aitole он оплачен, а кредиты не пришли за пару минут — выдай их вручную в «Участниках».',

  /** At most once per 10 minutes per kind of error. */
  ownerProviderError: (provider: string, detail: string): string =>
    `⚠️ Ошибка платёжки ${provider} при выставлении счёта:\n${detail}`.slice(0, 3500),
};

export type Messages = typeof ru;
